import {
    ComponentRef,
    Directive,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ProgressTrackerService } from 'src/app/_shared/components/progress/services/progress-tracker.service';
import { ProgressOverlayComponent } from 'src/app/_shared/components/progress/components/progress-overlay/progress-overlay.component';
import { ProgressStatus } from 'src/app/_shared/components/progress/models/progress-definitions';

@Directive({
    selector: '[appProgressOverlay]',
    standalone: false
})
export class ProgressOverlayDirective implements OnInit, OnDestroy {

    /**
     * Filtre par catégorie d'opération.
     * Si défini, n'affiche que les opérations de ces catégories.
     * Ex: [appProgressOverlay]="['report']" ou [appProgressOverlay]="['export']"
     */
    @Input() appProgressOverlay: string[] | null = null;

    private readonly progressTrackerService = inject(ProgressTrackerService);
    private readonly vcr = inject(ViewContainerRef);
    private readonly elementRef = inject(ElementRef);
    private readonly renderer = inject(Renderer2);

    private readonly destroy$ = new Subject<void>();
    private componentRef: ComponentRef<ProgressOverlayComponent> | null = null;

    ngOnInit(): void {
        // S'assurer que l'élément hôte a position: relative pour le positionnement absolu de l'overlay
        this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');

        // Écouter les changements de status pour créer/détruire le composant
        this.progressTrackerService.progress$.pipe(
            takeUntil(this.destroy$),
            map(state => state.status),
            distinctUntilChanged()
        ).subscribe(status => this.handleStatusChange(status));
    }

    private handleStatusChange(status: ProgressStatus): void {
        const shouldShow = ['running', 'completed', 'error'].includes(status);

        if (shouldShow && !this.componentRef) {
            this.createOverlay();
        } else if (!shouldShow && this.componentRef) {
            this.destroyOverlay();
        }
    }

    private createOverlay(): void {
        // Vérifier le filtre de catégorie
        const config = this.progressTrackerService.currentConfig;
        if (this.appProgressOverlay?.length > 0 && config?.category) {
            if (!this.appProgressOverlay.includes(config.category)) {
                return;
            }
        }

        // Créer le composant
        this.componentRef = this.vcr.createComponent(ProgressOverlayComponent);

        // Passer les inputs
        this.componentRef.instance.state$ = this.progressTrackerService.progress$;
        this.componentRef.instance.config$ = this.progressTrackerService.currentConfig$;
        this.componentRef.instance.categories = this.appProgressOverlay;

        // Insérer le composant dans l'élément hôte (pas dans le ViewContainerRef)
        this.renderer.appendChild(
            this.elementRef.nativeElement,
            this.componentRef.location.nativeElement
        );
    }

    private destroyOverlay(): void {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroyOverlay();
    }
}
